import { LegalPage } from "../components/LegalPage";

const LAST_UPDATED = "2026-10-22";

const SECTIONS = [
  {
    title: "Our Commitment to You",
    body: () => (
      <>
        <p>
          We take every complaint seriously. If something has fallen short of what you
          expected from Elin's Consultancy, we want to understand what happened, put it
          right where we can, and learn from it so it doesn't happen again.
        </p>
        <p>
          This procedure explains how to raise a complaint, who will handle it, how long
          each step takes and what to do if you are still not satisfied at the end.
        </p>
      </>
    ),
  },
  {
    title: "How to Make a Complaint",
    body: ({ email, phone, address }) => (
      <>
        <p>Please raise your complaint in whichever way is easiest for you:</p>
        <ul>
          <li>
            <strong>By email</strong> to{" "}
            <a href={`mailto:${email}`}>{email}</a> with "Complaint" in the subject line.
            This is usually the fastest route and gives us a clear written record.
          </li>
          {phone && (
            <li>
              <strong>By phone</strong> on{" "}
              <a href={`tel:${phone.replace(/[^+\d]/g, "")}`}>{phone}</a>. We will listen,
              take notes and ask you to confirm your complaint in writing so we are sure
              we have captured it correctly.
            </li>
          )}
          {address && (
            <li>
              <strong>By post</strong> to {address.split("\n")[0]}
              {address.includes("\n") ? "," : ""}{" "}
              {address.split("\n").slice(1).join(", ")}, marked for the attention of
              "The Director — Complaints".
            </li>
          )}
        </ul>
        <p>To help us investigate quickly, please tell us:</p>
        <ul>
          <li>Your full name and the best way to contact you.</li>
          <li>The property or engagement your complaint relates to, and the date(s) involved.</li>
          <li>A clear summary of what has gone wrong, in your own words.</li>
          <li>What outcome you are hoping for.</li>
        </ul>
      </>
    ),
  },
  {
    title: "Who Receives Your Complaint",
    body: ({ email }) => (
      <>
        <p>
          All complaints are received and handled by the Director of Elin's Consultancy.
          If your complaint is about the Director personally, you can mark your email
          "<strong>Private &amp; Confidential — Independent Review</strong>" and send it
          to the same address at <a href={`mailto:${email}`}>{email}</a>; it will be
          routed to an independent reviewer within the business.
        </p>
      </>
    ),
  },
  {
    title: "Acknowledgement — Within 3 Working Days",
    body: () => (
      <>
        <p>
          We will acknowledge your complaint in writing within <strong>3 working days</strong>{" "}
          of receiving it. The acknowledgement will:
        </p>
        <ul>
          <li>Confirm the name of the person investigating your complaint.</li>
          <li>Summarise the issues as we understand them, so we can agree we have the right picture.</li>
          <li>Explain the next steps and the timeframe to expect a full response.</li>
          <li>Ask any clarifying questions we need to investigate properly.</li>
        </ul>
      </>
    ),
  },
  {
    title: "Investigation — Within 14 Working Days",
    body: () => (
      <>
        <p>
          Within <strong>14 working days</strong> of acknowledging the complaint we aim to
          complete our investigation. During this time we will:
        </p>
        <ul>
          <li>Review the file, emails, invoices and any work product relating to the matter.</li>
          <li>Speak to anyone from Elin's Consultancy who was involved.</li>
          <li>Where helpful, speak to any third party we introduced you to (with your permission).</li>
          <li>Keep you updated at least once a week on progress.</li>
        </ul>
        <p>
          If the matter is particularly complex and we need longer, we will tell you in
          writing within those 14 working days, explain why, and give a new target date.
          Any extension will be no more than a further <strong>14 working days</strong>{" "}
          unless we agree otherwise with you.
        </p>
      </>
    ),
  },
  {
    title: "Our Response",
    body: () => (
      <>
        <p>At the end of the investigation we will send you a written response which sets out:</p>
        <ul>
          <li>What we looked at and who we spoke to.</li>
          <li>Our findings on each point you raised.</li>
          <li>Where we accept something went wrong, a clear apology.</li>
          <li>What we propose to do to put it right — this may include correcting the work, a reduction in fees, a refund, or improvements to our processes so it doesn't happen again.</li>
          <li>How to let us know whether this resolves things for you.</li>
        </ul>
        <p>
          If we don't uphold a point, we will explain why so you can see our reasoning.
        </p>
      </>
    ),
  },
  {
    title: "If You're Still Not Satisfied",
    body: ({ email }) => (
      <>
        <p>
          Please tell us. Write back within <strong>30 days</strong> of our response and
          explain which points remain unresolved and why. A senior member of Elin's
          Consultancy who was not involved in the original investigation will then carry
          out a final internal review and respond within a further <strong>14 working
          days</strong>. Please email <a href={`mailto:${email}`}>{email}</a> with
          "Final Review" in the subject line.
        </p>
        <p>
          If, after our final review, you are still not satisfied, you may be able to
          escalate the matter outside the business:
        </p>
        <ul>
          <li>
            For <strong>data protection complaints</strong>, you can contact the Information
            Commissioner's Office at{" "}
            <a href="https://ico.org.uk/" target="_blank" rel="noreferrer">ico.org.uk</a>.
          </li>
          <li>
            For complaints about an <strong>introduced professional</strong> (such as a
            solicitor, broker or surveyor), their own regulator or redress scheme will have
            an independent complaints process. We will tell you which one applies and share
            the right contact point.
          </li>
          <li>
            Nothing in this procedure affects your right to seek independent legal advice
            or to pursue the matter through the courts of England and Wales.
          </li>
        </ul>
      </>
    ),
  },
  {
    title: "How We Record and Learn",
    body: () => (
      <>
        <p>
          Every complaint is logged with the date received, who handled it, the outcome
          and any process change it led to. We review the log at least annually to spot
          themes and to improve how we work. Your personal data in that log is held in line
          with our <a href="/privacy-policy">Privacy Policy</a>.
        </p>
      </>
    ),
  },
];

const ComplaintsProcedure = () => (
  <LegalPage
    metaTitle="Complaints Procedure | Elin's Consultancy"
    metaDescription="How to raise a complaint with Elin's Consultancy, who will handle it, our timelines, and what to do if you remain unsatisfied."
    eyebrow="Legal"
    titleLines={[
      "Complaints",
      <>
        <em className="italic font-normal text-gold-soft">Procedure</em>
      </>,
    ]}
    intro="How to raise a complaint, how we investigate it, and what to do if you're still unhappy at the end."
    lastUpdated={LAST_UPDATED}
    sections={SECTIONS}
  />
);

export default ComplaintsProcedure;
